import { useTranslation } from "react-i18next";
import { useLocale } from "@/hooks/useLocale";
import type { TFunction } from "i18next";
import { formatPerson } from "@/utils/helper";
import { useMemo, useState } from "react";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useGetWorkflowInstanceAuditQuery } from "@/services/(company)/workflowApi";
import type {
  IWorkflowAuditEntry,
  WorkflowAuditEventType,
} from "@/types/(company)/workflow.interface";
import {
  AlertOctagon,
  ArrowRight,
  Bot,
  CheckCircle2,
  Clock,
  Cog,
  GitBranch,
  PlayCircle,
  RefreshCw,
  Shield,
  ShieldAlert,
  Sparkles,
  Undo2,
  UserPlus,
  UserSquare,
  UserX,
  XCircle,
} from "lucide-react";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import isToday from "dayjs/plugin/isToday";
import isYesterday from "dayjs/plugin/isYesterday";
import { startCase } from "lodash";

dayjs.extend(relativeTime);
dayjs.extend(isToday);
dayjs.extend(isYesterday);

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  instanceId: number | null;
}

/** Icon + tone classes for an event type missing from the meta map. */
const FALLBACK_EVENT_STYLE = {
  icon: Sparkles,
  tone: "text-gray-600",
  bg: "bg-gray-100",
};

const buildEventMeta = (
  t: TFunction,
): Record<
  WorkflowAuditEventType,
  { label: string; icon: typeof Sparkles; tone: string; bg: string }
> => ({
  created: {
    label: t("company:workflows.event_request_created"),
    icon: Sparkles,
    tone: "text-primary",
    bg: "bg-primary/10",
  },
  step_advanced: {
    label: t("company:workflows.event_step_advanced"),
    icon: PlayCircle,
    tone: "text-info",
    bg: "bg-info-muted",
  },
  approved: {
    label: t("company:workflows.event_approved"),
    icon: CheckCircle2,
    tone: "text-emerald-600",
    bg: "bg-emerald-50",
  },
  rejected: {
    label: t("company:workflows.event_rejected"),
    icon: XCircle,
    tone: "text-red-600",
    bg: "bg-red-50",
  },
  cancelled: {
    label: t("company:workflows.event_cancelled"),
    icon: XCircle,
    tone: "text-gray-500",
    bg: "bg-gray-100",
  },
  changes_requested: {
    label: t("company:workflows.event_changes_requested"),
    icon: Undo2,
    tone: "text-amber-600",
    bg: "bg-amber-50",
  },
  resubmitted: {
    label: t("company:workflows.event_resubmitted"),
    icon: RefreshCw,
    tone: "text-info",
    bg: "bg-info-muted",
  },
  delegated: {
    label: t("company:workflows.event_delegated"),
    icon: UserPlus,
    tone: "text-primary",
    bg: "bg-primary",
  },
  overdue_marked: {
    label: t("company:workflows.event_overdue_marked"),
    icon: Clock,
    tone: "text-amber-700",
    bg: "bg-amber-50",
  },
  escalated: {
    label: t("company:workflows.event_escalated"),
    icon: GitBranch,
    tone: "text-orange-600",
    bg: "bg-orange-50",
  },
  orphan_reassigned: {
    label: t("company:workflows.event_orphan_reassigned"),
    icon: UserX,
    tone: "text-amber-700",
    bg: "bg-amber-50",
  },
  error_stamped: {
    label: t("company:workflows.event_error_stamped"),
    icon: AlertOctagon,
    tone: "text-red-700",
    bg: "bg-red-50",
  },
  error_cleared: {
    label: t("company:workflows.event_error_cleared"),
    icon: Shield,
    tone: "text-emerald-600",
    bg: "bg-emerald-50",
  },
  admin_override: {
    label: t("company:workflows.event_admin_override"),
    icon: ShieldAlert,
    tone: "text-purple-700",
    bg: "bg-primary",
  },
});

// Filter pill groups — driven by what the timeline actually contains.
const buildFilterGroups = (
  t: TFunction,
): {
  key: "all" | "actions" | "system";
  label: string;
  icon: typeof Sparkles;
  hint: string;
}[] => [
  {
    key: "all",
    label: t("company:workflows.filter_all_activity"),
    icon: Sparkles,
    hint: t("company:workflows.filter_all_activity_hint"),
  },
  {
    key: "actions",
    label: t("company:workflows.filter_manual_actions"),
    icon: UserSquare,
    hint: t("company:workflows.filter_manual_actions_hint"),
  },
  {
    key: "system",
    label: t("company:workflows.filter_automatic"),
    icon: Cog,
    hint: t("company:workflows.filter_automatic_hint"),
  },
];

const initialsOf = (name?: string | null) => {
  if (!name) return "";
  return name
    .trim()
    .split(/\s+/)
    .map((s) => s[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();
};

const Person = ({
  user,
  size = "sm",
}: {
  user: { firstName?: string; lastName?: string; profileImage?: string | null };
  size?: "xs" | "sm";
}) => {
  const { t } = useTranslation(["common", "company"]);
  const name = formatPerson(user, t("company:workflows.someone_fallback"));
  const sz = size === "xs" ? "size-5" : "size-6";
  const text = size === "xs" ? "text-[10px]" : "text-[11px]";
  return (
    <span className="inline-flex items-center gap-1.5 align-middle">
      <Avatar className={`${sz} border border-white shadow-xs`}>
        <AvatarImage src={user.profileImage ?? ""} alt={name} />
        <AvatarFallback className="bg-primary/10 text-primary text-[9px] font-semibold">
          {initialsOf(name)}
        </AvatarFallback>
      </Avatar>
      <span className={`${text} font-medium text-gray-800`}>{name}</span>
    </span>
  );
};

const SystemActor = ({ actorType }: { actorType: string }) => {
  const { t } = useTranslation(["common", "company"]);
  const Icon =
    actorType === "cron" ? Clock : actorType === "engine" ? Bot : Cog;
  const label =
    actorType === "cron"
      ? t("company:workflows.scheduled_job")
      : actorType === "engine"
        ? t("company:workflows.system_safeguard")
        : t("company:workflows.filter_automatic");
  return (
    <span className="inline-flex items-center gap-1 text-[11px] text-gray-500 italic align-middle">
      <Icon className="size-3" />
      {label}
    </span>
  );
};

// Renders the from→to for nodes/approvers so reviewers can see what
// actually changed without staring at IDs.
const TransitionLine = ({ entry }: { entry: IWorkflowAuditEntry }) => {
  const { t } = useTranslation(["company"]);
  const nodeChanged =
    entry.fromNode && entry.toNode && entry.fromNode.id !== entry.toNode.id;
  const approverChanged =
    entry.fromApprover &&
    entry.toApprover &&
    entry.fromApprover.id !== entry.toApprover.id;
  const statusChanged =
    entry.fromStatus && entry.toStatus && entry.fromStatus !== entry.toStatus;

  if (!nodeChanged && !approverChanged && !statusChanged) return null;

  return (
    <div className="flex flex-col gap-1 mt-1.5 text-[11px] text-gray-700">
      {nodeChanged && (
        <div className="flex items-center gap-1.5 flex-wrap">
          <span className="text-gray-500 shrink-0">
            {t("company:workflows.audit_step_label")}
          </span>
          <span className="font-medium">{entry.fromNode!.name}</span>
          <ArrowRight className="size-3 text-gray-400 shrink-0" />
          <span className="font-medium text-gray-900">
            {entry.toNode!.name}
          </span>
        </div>
      )}
      {approverChanged && (
        <div className="flex items-center gap-1.5 flex-wrap">
          <span className="text-gray-500 shrink-0">
            {t("company:workflows.audit_approver_label")}
          </span>
          <Person user={entry.fromApprover!} size="xs" />
          <ArrowRight className="size-3 text-gray-400 shrink-0" />
          <Person user={entry.toApprover!} size="xs" />
        </div>
      )}
      {statusChanged && !nodeChanged && (
        <div className="flex items-center gap-1.5 flex-wrap">
          <span className="text-gray-500 shrink-0">
            {t("company:workflows.audit_status_label")}
          </span>
          <span className="font-medium">{startCase(entry.fromStatus!)}</span>
          <ArrowRight className="size-3 text-gray-400 shrink-0" />
          <span className="font-medium text-gray-900">
            {startCase(entry.toStatus!)}
          </span>
        </div>
      )}
    </div>
  );
};

const dayLabel = (
  d: dayjs.Dayjs,
  t: TFunction,
  formatDate: (date: Date, pattern: string) => string,
): string => {
  if (d.isToday()) return t("company:workflows.today_label");
  if (d.isYesterday()) return t("company:workflows.yesterday_label");
  return formatDate(d.toDate(), "MMM dd, yyyy");
};

const WorkflowAuditDialog = ({ open, onOpenChange, instanceId }: Props) => {
  const { t } = useTranslation(["common", "company"]);
  const { formatDate } = useLocale();
  const EVENT_META = buildEventMeta(t);
  const FILTER_GROUPS = buildFilterGroups(t);
  const [filterKey, setFilterKey] = useState<"all" | "actions" | "system">(
    "all",
  );

  // Map filter pill to backend params
  const queryArgs = useMemo(() => {
    const base = { instanceId: instanceId ?? 0 };
    switch (filterKey) {
      case "actions":
        return { ...base, actorTypes: ["user"] };
      case "system":
        return { ...base, actorTypes: ["cron", "engine", "system"] };
      case "all":
      default:
        return base;
    }
  }, [filterKey, instanceId]);

  const { data, isFetching, isError } = useGetWorkflowInstanceAuditQuery(
    queryArgs,
    { skip: !open || !instanceId },
  );

  const entries = data?.data ?? [];
  const isLoading = isFetching && !data;

  // Group by day for sticky day headers — keeps long timelines scannable.
  const groups = useMemo(() => {
    const map = new Map<string, IWorkflowAuditEntry[]>();
    for (const entry of entries) {
      const key = dayjs(entry.createdAt).format("YYYY-MM-DD");
      const bucket = map.get(key) ?? [];
      bucket.push(entry);
      map.set(key, bucket);
    }
    return Array.from(map.entries()).map(([key, items]) => ({
      key,
      label: dayLabel(dayjs(key), t, formatDate),
      items,
    }));
  }, [entries, t, formatDate]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[680px] max-h-[88vh] overflow-hidden p-0 flex flex-col">
        <DialogHeader className="px-6 pt-6 pb-4 border-b border-border">
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1 min-w-0">
              <DialogTitle className="text-lg font-semibold leading-tight">
                {t("company:workflows.activity_history_title")}
              </DialogTitle>
              <DialogDescription className="text-xs text-gray-600 mt-1">
                {t("company:workflows.activity_history_desc")}
              </DialogDescription>
            </div>
            {entries.length > 0 && (
              <Badge
                variant="neutral"
                dot={false}
                title={t("common:a11y.total_events_shown")}
              >
                {t("company:workflows.event_count", { count: entries.length })}
              </Badge>
            )}
          </div>

          <div className="flex flex-wrap items-center gap-1.5 mt-3">
            {FILTER_GROUPS.map((g) => {
              const Icon = g.icon;
              const active = filterKey === g.key;
              return (
                <button
                  key={g.key}
                  type="button"
                  onClick={() => setFilterKey(g.key)}
                  data-tooltip-id="hrm-tooltip"
                  data-tooltip-content={g.hint}
                  className={
                    "inline-flex items-center gap-1 px-2.5 py-1 rounded-full border text-[11px] font-medium transition-colors " +
                    (active
                      ? "bg-primary text-white border-primary"
                      : "bg-white text-gray-700 border-border hover:bg-gray-50")
                  }
                >
                  <Icon className="size-3" />
                  {g.label}
                </button>
              );
            })}
          </div>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto px-6 py-4 bg-gray-50/40">
          {isLoading ? (
            <div className="flex flex-col gap-3">
              {[0, 1, 2].map((i) => (
                <Skeleton key={i} className="h-20 w-full rounded-md" />
              ))}
            </div>
          ) : isError ? (
            <div className="rounded-md border border-red-200 bg-red-50 p-4 text-center">
              <p className="text-sm text-red-700">
                {t("company:workflows.couldnt_load_activity_history")}
              </p>
            </div>
          ) : entries.length === 0 ? (
            <div className="rounded-md border border-dashed border-border bg-white p-6 text-center">
              <Clock className="size-6 text-gray-300 mx-auto mb-1" />
              <p className="text-xs text-gray-500">
                {filterKey === "all"
                  ? t("company:workflows.no_activity_recorded_yet")
                  : t("company:workflows.no_events_match_filter")}
              </p>
            </div>
          ) : (
            <div className="flex flex-col gap-4">
              {groups.map((group) => (
                <section key={group.key}>
                  <div className="sticky top-0 z-10 bg-gray-50/80 backdrop-blur-xs py-1 px-1 mb-1.5">
                    <span className="text-[11px] uppercase tracking-wide font-semibold text-gray-500">
                      {group.label}
                    </span>
                  </div>
                  <ol className="flex flex-col gap-1.5">
                    {group.items.map((entry, idx) => {
                      const meta =
                        EVENT_META[entry.eventType] ??
                        ({
                          label: startCase(entry.eventType),
                          ...FALLBACK_EVENT_STYLE,
                        } as (typeof EVENT_META)[WorkflowAuditEventType]);
                      const Icon = meta.icon;
                      const isLast = idx === group.items.length - 1;
                      return (
                        <li key={entry.id} className="relative ps-9">
                          {!isLast && (
                            <span className="absolute start-[13px] top-7 bottom-[-6px] w-px bg-border" />
                          )}
                          <span
                            className={`absolute start-0 top-1.5 size-7 rounded-full border border-border flex items-center justify-center ${meta.bg} ${meta.tone}`}
                          >
                            <Icon className="size-3.5" />
                          </span>
                          <div className="rounded-md border border-border bg-white p-3">
                            <div className="flex items-center justify-between gap-2 flex-wrap">
                              <h4 className="text-sm font-semibold text-gray-900">
                                {meta.label}
                              </h4>
                              <span
                                className="text-[11px] text-gray-500"
                                title={formatDate(
                                  new Date(entry.createdAt),
                                  "MMM dd, yyyy · hh:mm a",
                                )}
                              >
                                {formatDate(
                                  new Date(entry.createdAt),
                                  "hh:mm a",
                                )}
                              </span>
                            </div>
                            <TransitionLine entry={entry} />
                            {entry.reason && (
                              <p className="text-[11px] text-gray-700 mt-2 bg-gray-50 border border-gray-100 rounded px-2 py-1.5 whitespace-pre-wrap">
                                {entry.reason}
                              </p>
                            )}
                            <div className="mt-2">
                              {entry.actor ? (
                                <Person user={entry.actor} />
                              ) : (
                                <SystemActor actorType={entry.actorType} />
                              )}
                            </div>
                          </div>
                        </li>
                      );
                    })}
                  </ol>
                </section>
              ))}
            </div>
          )}
        </div>

        <div className="border-t border-border bg-white px-6 py-3 flex justify-end">
          <DialogClose asChild>
            <Button variant="outline" size="sm">
              {t("company:workflows.close_action")}
            </Button>
          </DialogClose>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default WorkflowAuditDialog;
